/* URBRAND Street Commerce 5.9.77 — UI/UX runtime safeguards. */
(() => {
  'use strict';

  const SELECTOR_INVALID_FIELDS = 'input:not([type="hidden"]):invalid, select:invalid, textarea:invalid';
  const initializedForms = new WeakSet();
  let mutationObserver;
  let cartResizeFrame = 0;

  const matchesOrFind = (root, selector) => {
    const results = [];
    if (root instanceof Element && root.matches(selector)) results.push(root);
    root.querySelectorAll?.(selector).forEach((node) => results.push(node));
    return results;
  };

  const focusWithoutJump = (node) => {
    if (!(node instanceof HTMLElement)) return;
    try { node.focus({ preventScroll: true }); } catch { node.focus(); }
    node.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center'
    });
  };

  const syncFieldValidity = (field) => {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) return;
    if (field.disabled || field.type === 'hidden') return;
    if (field.validity.valid) field.removeAttribute('aria-invalid');
    else if (field.dataset.uxValidated === 'true') field.setAttribute('aria-invalid', 'true');
  };

  const enhanceForm = (form) => {
    if (!(form instanceof HTMLFormElement) || initializedForms.has(form)) return;
    initializedForms.add(form);

    form.addEventListener('invalid', (event) => {
      const field = event.target;
      if (!(field instanceof HTMLElement)) return;
      field.dataset.uxValidated = 'true';
      field.setAttribute('aria-invalid', 'true');
    }, true);

    form.addEventListener('input', (event) => syncFieldValidity(event.target), true);
    form.addEventListener('change', (event) => syncFieldValidity(event.target), true);

    form.addEventListener('submit', (event) => {
      const invalidFields = [...form.querySelectorAll(SELECTOR_INVALID_FIELDS)];
      if (!invalidFields.length) return;
      event.preventDefault();
      invalidFields.forEach((field) => {
        field.dataset.uxValidated = 'true';
        field.setAttribute('aria-invalid', 'true');
      });
      focusWithoutJump(invalidFields[0]);
    });
  };

  const enhanceForms = (root = document) => matchesOrFind(root, 'form').forEach(enhanceForm);

  const hardenBlankLinks = (root = document) => {
    matchesOrFind(root, 'a[target="_blank"]').forEach((link) => {
      const rel = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
      rel.add('noopener');
      rel.add('noreferrer');
      link.setAttribute('rel', [...rel].join(' '));
    });
  };

  const syncCurrentPageLinks = (root = document) => {
    const current = new URL(window.location.href);
    const selector = [
      'nav a[href]:not([aria-current])',
      '[role="navigation"] a[href]:not([aria-current])',
      '.footer-links a[href]:not([aria-current])',
      '.customer-account-nav a[href]:not([aria-current])'
    ].join(',');
    matchesOrFind(root, selector).forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        if (url.origin === current.origin && url.pathname === current.pathname && !url.search && !url.hash && !current.search) {
          link.setAttribute('aria-current', 'page');
        }
      } catch { /* Malformed merchant URL: leave untouched. */ }
    });
  };

  const enhanceLiveRegions = (root = document) => {
    matchesOrFind(root, '[role="status"], [aria-live]').forEach((node) => {
      if (!node.hasAttribute('aria-atomic')) node.setAttribute('aria-atomic', 'true');
    });
  };

  const enhanceImages = (root = document) => {
    matchesOrFind(root, 'img:not([decoding]):not([fetchpriority="high"]):not([loading="eager"])')
      .forEach((image) => image.setAttribute('decoding', 'async'));
  };

  const enhanceCartScrollIndicator = (root = document) => {
    matchesOrFind(root, '[data-cart-body]').forEach((body) => {
      if (!(body instanceof HTMLElement)) return;
      const shell = body.closest('[data-cart-shell]');
      if (!(shell instanceof HTMLElement)) return;

      let track = shell.querySelector('[data-cart-scroll-indicator]');
      if (!track) {
        track = document.createElement('span');
        track.className = 'cart-drawer__scroll-indicator';
        track.dataset.cartScrollIndicator = '';
        track.setAttribute('aria-hidden', 'true');
        const thumb = document.createElement('span');
        thumb.className = 'cart-drawer__scroll-indicator-thumb';
        thumb.dataset.cartScrollThumb = '';
        track.append(thumb);
        shell.append(track);
      }

      const thumb = track.querySelector('[data-cart-scroll-thumb]');
      if (!(thumb instanceof HTMLElement)) return;

      const sync = () => {
        const viewport = body.clientHeight;
        const content = body.scrollHeight;
        const overflow = Math.max(0, content - viewport);
        const trackHeight = Math.max(0, viewport - 12);
        const isEmpty = body.classList.contains('cart-drawer__body--empty') || Boolean(body.querySelector('.cart-drawer__empty'));
        const canScroll = !isEmpty && overflow > 2 && trackHeight >= 36;

        track.hidden = !canScroll;
        shell.classList.toggle('cart-drawer--can-scroll', canScroll);
        if (!canScroll) return;

        track.style.top = `${body.offsetTop + 6}px`;
        track.style.height = `${trackHeight}px`;

        const thumbHeight = Math.max(32, Math.min(trackHeight, Math.round(trackHeight * (viewport / content))));
        const travel = Math.max(0, trackHeight - thumbHeight);
        const progress = overflow > 0 ? Math.min(1, Math.max(0, body.scrollTop / overflow)) : 0;

        thumb.style.height = `${thumbHeight}px`;
        thumb.style.transform = `translate3d(0, ${Math.round(travel * progress)}px, 0)`;
        shell.classList.toggle('cart-drawer--at-scroll-end', progress >= .985);
      };

      body.urbCartScrollSync = sync;
      if (body.dataset.uxScrollIndicator !== 'true') {
        body.dataset.uxScrollIndicator = 'true';
        body.addEventListener('scroll', sync, { passive: true });
        body.querySelectorAll('img').forEach((image) => {
          if (!image.complete) image.addEventListener('load', sync, { once: true });
        });
      }
      window.requestAnimationFrame(sync);
    });
  };

  const syncVisibleCartScrollIndicators = () => {
    document.querySelectorAll('[data-cart-body]').forEach((body) => body.urbCartScrollSync?.());
  };

  const init = (root = document) => {
    document.documentElement.classList.add('js', 'ux-ready');
    enhanceForms(root);
    hardenBlankLinks(root);
    syncCurrentPageLinks(root);
    enhanceLiveRegions(root);
    enhanceImages(root);
    enhanceCartScrollIndicator(root);
  };

  const observeDynamicContent = () => {
    if (mutationObserver || !document.body) return;
    mutationObserver = new MutationObserver((mutations) => {
      const roots = new Set();
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) roots.add(node);
      }));
      roots.forEach(init);
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const disabled = event.target.closest('[aria-disabled="true"]');
    if (disabled) event.preventDefault();
  });

  window.addEventListener('resize', () => {
    window.cancelAnimationFrame(cartResizeFrame);
    cartResizeFrame = window.requestAnimationFrame(syncVisibleCartScrollIndicators);
  }, { passive: true });

  document.addEventListener('urb:cart:rendered', () => window.requestAnimationFrame(() => {
    syncVisibleCartScrollIndicators();
  }));

  document.addEventListener('urb:cart:open', () => window.requestAnimationFrame(() => {
    syncVisibleCartScrollIndicators();
  }));

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('DOMContentLoaded', () => {
    init(document);
    observeDynamicContent();
  }, { once: true });
})();
